import { createSlice } from "@reduxjs/toolkit";
import { InstanceApi } from "../../helpers/InstanceApi";
import { Validate } from "../../helpers/Validate";
import { setPopup } from "../popupSlice";

export const workflowSlice = createSlice({
  name: "workflow",
  initialState: {
    openModal: false,
    dataTables: undefined,
    dataOptions: [],
    dataFilter: [],
    dataView: undefined,
    formValidate: {
      object: { required: true, title: "Object" },
      type: { required: true, title: "Type" },
    },
    formData: {
      id: "",
      object: "",
      type: "",
    },
    approval: false,
  },
  reducers: {
    resetDataView: (state) => {
      state.dataView = undefined;
    },
    setModal: (state, action) => {
      state.openModal = action.payload;
    },
    setDataTables: (state, action) => {
      state.dataTables = action.payload;
    },
    setDataOptions: (state, action) => {
      state.dataOptions = action.payload;
    },
    setDataFilter: (state, action) => {
      state.dataFilter = action.payload;
    },
    setDataView: (state, action) => {
      state.dataView = action.payload;
    },
    setFormData: (state, action) => {
      state.formData[action.payload.name] = action.payload.value;
    },
    setApproval: (state, action) => {
      state.approval = action.payload;
    },
  },
});

export const resetFormData = () => async (dispatch, getState) => {
  const formData = getState().workflow.formData;
  Object.keys(formData).forEach((key) => {
    dispatch(setFormData({ name: key, value: "" }));
  });
};

export const getDataTables = () => async (dispatch) => {
  try {
    dispatch(setDataTables(undefined));
    dispatch(setDataView(undefined));
    dispatch(setPopup({ open: true, type: "loading" }));

    const response = await InstanceApi.get("/wf/workflows");
    if (response) {
      dispatch(setDataTables(response.data.data));
      dispatch(setPopup({ open: false }));
    }
  } catch (error) {
    console.log(error);
  }
};

export const getDataOptions = () => async (dispatch) => {
  try {
    const response = await InstanceApi.get("/wf/workflows");
    if (response) {
      const data = response.data.data.map((item) => {
        return { value: item.id, label: item.workflow_name };
      });

      dispatch(setDataOptions(data));
    }
  } catch (error) {
    console.log(error);
  }
};

export const getDataView = (params) => async (dispatch) => {
  try {
    dispatch(setPopup({ open: true, type: "loading" }));

    const response = await InstanceApi.get("/wf/workflows/" + params.id);
    if (response) {
      dispatch(setDataView(response.data.data));
      dispatch(setPopup({ open: false }));
    }
  } catch (error) {
    dispatch(
      setPopup({
        open: true,
        type: "error",
        message: error.message,
        handleOk: () => {
          dispatch(setPopup({ open: false }));
        },
      })
    );
    console.error(error.response.data.message);
  }
};

export const getFormData = (params) => async (dispatch, getState) => {
  try {
    dispatch(setPopup({ open: true, type: "loading" }));

    const response = await InstanceApi.get("/wf/workflows/" + params.id);
    if (response) {
      const data = response.data.data;
      const formData = getState().workflow.formData;
      Object.keys(formData).forEach((key) => {
        dispatch(setFormData({ name: key, value: data?.[key] ?? "" }));
      });

      dispatch(setPopup({ open: false }));
    }
  } catch (error) {
    dispatch(
      setPopup({
        open: true,
        type: "error",
        message: error.message,
        handleOk: () => {
          dispatch(setPopup({ open: false }));
        },
      })
    );
    console.error(error.response.data.message);
  }
};

export const submit = (params) => async (dispatch, getState) => {
  try {
    dispatch(setPopup({ open: true, type: "processing" }));
    const formValidate = getState().workflow.formValidate;
    const formData = getState().workflow.formData;
    const validate = Validate(formValidate, formData);
    const formWorkflowDetail = getState().workflowDetail.dataTemporary;

    const submitForm = {
      workflow: formData,
      workflowDetail: formWorkflowDetail,
    };

    if (!validate.isValid) {
      dispatch(
        setPopup({
          open: true,
          type: "error",
          message: validate.message,
          handleOk: () => {
            dispatch(setPopup({ open: false }));
          },
        })
      );
    } else {
      let response = await InstanceApi.patch(
        "/wf/workflows/" + formData.id,
        submitForm
      );
      if (response) {
        dispatch(
          setPopup({
            open: true,
            type: "success",
            message: response.data.message,
            handleOk: () => {
              dispatch(setPopup({ open: false }));
              dispatch(setModal(false));
              dispatch(resetFormData());
              window.location.href = "/wf/workflows";
            },
          })
        );
      }
    }
  } catch (err) {
    dispatch(
      setPopup({
        open: true,
        type: "error",
        message: err.message,
        handleOk: () => {
          dispatch(setPopup({ open: false }));
        },
      })
    );
    console.error(err.response.data.message);
  }
};

export const deleteData = (params) => (dispatch) => {
  try {
    dispatch(
      setPopup({
        open: true,
        type: "delete",
        message: "Are you sure to delete this data?",
        handleOk: async () => {
          try {
            const response = await InstanceApi.delete(
              "/wf/workflows/" + params.id
            );
            if (response) {
              dispatch(
                setPopup({
                  open: true,
                  type: "success",
                  message: response.data.message,
                  handleOk: () => {
                    dispatch(setPopup({ open: false }));
                    dispatch(getDataTables());
                  },
                })
              );
            }
          } catch (error) {
            dispatch(
              setPopup({
                open: true,
                type: "error",
                message: error.message,
                handleOk: () => {
                  dispatch(setPopup({ open: false }));
                },
              })
            );
          }
        },
        handleCancel: () => {
          dispatch(setPopup({ open: false }));
        },
      })
    );
  } catch (err) {
    dispatch(
      setPopup({
        open: true,
        type: "error",
        message: err.message,
        handleOk: () => {
          dispatch(setPopup({ open: false }));
        },
      })
    );
  }
};

export const checkApproval = (params) => async (dispatch) => {
  try {
    const response = await InstanceApi.post("/wf/workflows/check-approval", {
      object: params.obj,
      type: params.type,
      object_id: params.objId,
    });
    if (response) {
      dispatch(setApproval(response.data.data));
    }
  } catch (err) {
    dispatch(setApproval(false));
  }
};

export const checkApprovalDirect = (params) => async (dispatch) => {
  try {
    const response = await InstanceApi.post("/wf/workflows/check-approval", {
      object: params.obj,
      type: params.type,
      object_id: params.objId,
    });
    if (response) {
      return response.data.data;
    }
  } catch (err) {
    return false;
  }
};

export const {
  resetDataView,
  setModal,
  setDataTables,
  setDataOptions,
  setDataFilter,
  setDataView,
  setFormData,
  setApproval,
} = workflowSlice.actions;
export default workflowSlice.reducer;
